import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '.';
import { getOidcApiRoot } from '../env-vars';
import AuthService, { IAuthService } from '../services/AuthService';

let authService: IAuthService;
let pendingTokenRefresh: Promise<void> | null = null;

// oidc-client-ts never times out a popup sign-in, so an opened-but-ignored popup
// (e.g. from a background token refresh the user didn't notice) would hang forever.
const POPUP_SIGNIN_TIMEOUT_MS = 300_000;

export interface SerializedError {
  name?: string;
  message?: string;
  stack?: string;
  code?: string;
}

// Opens a single sign-in popup (timing out if left unattended) and falls back to a
// redirect login on failure. Shared via pendingTokenRefresh so concurrent requests
// (e.g. select-account's parallel queries) await one attempt instead of each racing
// to open their own popup, which is what left some requests hanging indefinitely.
function refreshExpiringToken(
  authSvc: IAuthService,
  currentLocation: string,
): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    const timer = setTimeout(finish, POPUP_SIGNIN_TIMEOUT_MS);

    authSvc
      .loginPopup(currentLocation)
      .then(() => {
        clearTimeout(timer);
        // The popup only refreshes the base OIDC token — it does not carry
        // B2B/2FA claims (b2b_aid, rbac_2fa_raids). Reload so
        // ProtectedHubRoute / AdminPortalInitializer detect the missing
        // claims and re-run their enrichment flow automatically.
        window.location.reload();
        finish();
      })
      .catch((error: any) => {
        clearTimeout(timer);
        window.localStorage.removeItem('signinpopup');
        // ignore state_mismatch error because we are doing 2FA and use the token from localstorage
        if (error?.message !== 'No matching state found in storage') {
          authSvc.login(currentLocation);
        }
        finish();
      });
  });
}

// initialize an empty api service that we'll inject endpoints into later as needed
export const emptySliceApi = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: `${getOidcApiRoot()}/api`,
    credentials: 'include',
    prepareHeaders: async (headers, { getState }) => {
      if (!authService) {
        authService = new AuthService();
      }
      // By default, if we have a token in the store, let's use that for authenticated requests
      let user = await authService.getUser();
      const currentLocation = window.location.pathname + window.location.search;
      if (!user) {
        authService.login(currentLocation);
      }
      // prompt before 10 mins
      const currentTime = Date.now().valueOf();
      const popupTime = currentTime / 1000 + 10 * 60;

      if (
        user?.expires_at <= popupTime &&
        window.localStorage.getItem('signinpopup') !== 'open' &&
        !window.opener
      ) {
        pendingTokenRefresh ??= refreshExpiringToken(
          authService,
          currentLocation,
        ).finally(() => {
          pendingTokenRefresh = null;
        });
        await pendingTokenRefresh;
        user = await authService.getUser();
      }
      const currentSelectedAccountId = (getState() as RootState).account
        ?.currentAccountId;
      if (user && !headers.has('authorization')) {
        headers.set('authorization', `Bearer ${user.access_token}`);
      }
      if (currentSelectedAccountId) {
        headers.set('X-SelectedAccountId', currentSelectedAccountId);
      }
      return headers;
    },
  }),
  tagTypes: [
    'Admin',
    'User',
    'Domain',
    'Deal',
    'Group',
    'AccountData',
    'Report',
    'AccountAlerts',
    'CustomAccountAlerts',
    'HubUserConsentData',
    'UserPreference',
    'UserSettings',
    'Diagnostics',
    'CommentItem',
    'Product',
    'CartProducts',
    'Address',
    'PhoneNumber',
    'AccessRule',
    'DomainProducts',
    'SavedPayment',
  ],
  endpoints: () => ({}),
});

export default emptySliceApi;
